import { useState, useEffect, useCallback } from 'react'
import { Plus, Save, X, FileText, ChevronLeft, Trash2, ExternalLink, Download, ChevronDown } from 'lucide-react'
import { supabase } from './supabase'
import { useToast } from './ToastContext'
import { format } from 'date-fns'
import { SERVICE_AREAS, APPROVAL_PATHS, isActiveProfile } from './constants'
import jsPDF from 'jspdf'
import {
  Document, Packer, Paragraph, HeadingLevel, TextRun, AlignmentType,
} from 'docx'

const MEETING_TYPES = [
  { value: 'kickoff', label: 'Kick-off', icon: '🚀' },
  { value: 'weekly', label: 'Weekly', icon: '📅' },
  { value: 'strategy', label: 'Strategy', icon: '🎯' },
  { value: 'adhoc', label: 'Ad Hoc', icon: '💬' },
]

const TEMPLATES = {
  kickoff: `# Kick-off Meeting

## Attendees


## Client Background & Goals


## Services We're Providing


## Timeline Overview


## Client Questions & Answers


## Action Items
- [ ] 
- [ ] 

## Decisions Made

`,
  weekly: `# Weekly Check-in

## Wins Since Last Meeting


## Data Highlights


## Current Task Status


## Client Feedback


## What's Next


## Action Items
- [ ] 
- [ ] 

`,
  strategy: `# Strategy Session

## Context / Problem We're Solving


## Options Discussed


## Decisions Made


## Next Steps
- [ ] 
- [ ] 

## Notes

`,
  adhoc: '',
}

// ─── Simple rich text toolbar ─────────────────────────────────────────────────
function RichTextEditor({ value, onChange }) {
  const apply = (prefix, suffix = '') => {
    const textarea = document.getElementById('meeting-editor')
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const selected = value.slice(start, end)
    const newVal = value.slice(0, start) + prefix + selected + suffix + value.slice(end)
    onChange(newVal)
    setTimeout(() => {
      textarea.focus()
      textarea.setSelectionRange(start + prefix.length, end + prefix.length)
    }, 0)
  }

  const insertLine = (prefix) => {
    const textarea = document.getElementById('meeting-editor')
    const start = textarea.selectionStart
    const lineStart = value.lastIndexOf('\n', start - 1) + 1
    const newVal = value.slice(0, lineStart) + prefix + value.slice(lineStart)
    onChange(newVal)
    setTimeout(() => textarea.focus(), 0)
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 4, padding: '8px 12px', background: 'var(--cream)', borderBottom: '1px solid var(--border)', flexWrap: 'wrap' }}>
        {[
          { label: 'B', action: () => apply('**', '**'), title: 'Bold', style: { fontWeight: 700 } },
          { label: 'I', action: () => apply('_', '_'), title: 'Italic', style: { fontStyle: 'italic' } },
          { label: 'H1', action: () => insertLine('# '), title: 'Heading 1' },
          { label: 'H2', action: () => insertLine('## '), title: 'Heading 2' },
          { label: 'H3', action: () => insertLine('### '), title: 'Heading 3' },
          { label: '• List', action: () => insertLine('- '), title: 'Bullet list' },
          { label: '☐ Todo', action: () => insertLine('- [ ] '), title: 'Checkbox' },
        ].map(btn => (
          <button key={btn.label} onClick={btn.action} title={btn.title}
            style={{ padding: '3px 8px', border: '1px solid var(--border)', borderRadius: 4, background: '#fff', fontSize: 12, cursor: 'pointer', ...btn.style }}>
            {btn.label}
          </button>
        ))}
      </div>
      <textarea
        id="meeting-editor"
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{
          width: '100%', minHeight: 400, padding: '16px', border: 'none',
          fontFamily: 'var(--font-body)', fontSize: 14, lineHeight: 1.7,
          resize: 'vertical', outline: 'none', background: '#fff',
        }}
        placeholder="Start typing your meeting notes..."
      />
    </div>
  )
}

// ─── Render markdown-ish preview ─────────────────────────────────────────────
function NotePreview({ content }) {
  if (!content) return <p style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>No notes yet</p>

  const lines = content.split('\n')
  return (
    <div style={{ lineHeight: 1.7, fontSize: 14 }}>
      {lines.map((line, i) => {
        if (line.startsWith('# ')) return <h2 key={i} style={{ fontFamily: 'var(--font-display)', fontSize: 20, color: 'var(--navy)', margin: '16px 0 6px' }}>{line.slice(2)}</h2>
        if (line.startsWith('## ')) return <h3 key={i} style={{ fontFamily: 'var(--font-display)', fontSize: 16, color: 'var(--navy)', margin: '14px 0 4px' }}>{line.slice(3)}</h3>
        if (line.startsWith('### ')) return <h4 key={i} style={{ fontSize: 14, fontWeight: 600, color: 'var(--navy)', margin: '10px 0 2px' }}>{line.slice(4)}</h4>
        if (line.startsWith('- [ ] ')) return <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, margin: '2px 0' }}><input type="checkbox" readOnly /><span>{line.slice(6)}</span></div>
        if (line.startsWith('- [x] ')) return <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, margin: '2px 0', color: 'var(--text-muted)' }}><input type="checkbox" readOnly checked /><span style={{ textDecoration: 'line-through' }}>{line.slice(6)}</span></div>
        if (line.startsWith('- ')) return <div key={i} style={{ paddingLeft: 16, margin: '2px 0' }}>• {line.slice(2)}</div>
        if (line === '') return <div key={i} style={{ height: 8 }} />
        // Bold
        const boldParsed = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/_(.*?)_/g, '<em>$1</em>')
        return <p key={i} style={{ margin: '2px 0' }} dangerouslySetInnerHTML={{ __html: boldParsed }} />
      })}
    </div>
  )
}

// ─── Quick task form within notes ─────────────────────────────────────────────
function QuickTaskForm({ clientId, meetingNoteId, profiles, clients, currentUser, onSaved, onClose }) {
  const toast = useToast()
  const [title, setTitle] = useState('')
  const [assignedTo, setAssignedTo] = useState('')
  const [serviceArea, setServiceArea] = useState('website')
  const [dateDue, setDateDue] = useState('')
  const [weekNumber, setWeekNumber] = useState('')
  const [approvalPath, setApprovalPath] = useState('internal')
  const [isOnboarding, setIsOnboarding] = useState(false)
  const [saving, setSaving] = useState(false)

  const save = async () => {
    if (!title.trim()) { toast('Title required', 'error'); return }
    setSaving(true)
    const { data: task, error } = await supabase.from('tasks').insert({
      title: title.trim(),
      client_id: clientId,
      meeting_note_id: meetingNoteId,
      assigned_to: assignedTo || null,
      assigned_by: currentUser.id,
      service_area: serviceArea,
      date_due: dateDue || null,
      week_number: weekNumber ? parseInt(weekNumber) : null,
      approval_path: approvalPath,
      is_onboarding_task: isOnboarding,
      status: 'not_started',
      client_visible: true,
    }).select().single()

    if (error) { toast('Error: ' + error.message, 'error'); setSaving(false); return }

    if (task?.id) supabase.functions.invoke('sync-tasks-to-sheets', { body: { task_id: task.id } })

    toast('Task created!', 'success')
    onSaved(task)
  }

  return (
    <div style={{ background: 'var(--cream)', border: '1px solid var(--border)', borderRadius: 8, padding: 14, marginTop: 10 }}>
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, color: 'var(--navy)' }}>Add Task from this Meeting</div>
      <div className="form-group">
        <input className="form-control" value={title} onChange={e => setTitle(e.target.value)} placeholder="Task title..." autoFocus onKeyDown={e => e.key === 'Enter' && save()} />
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Assign To</label>
          <select className="form-control" value={assignedTo} onChange={e => setAssignedTo(e.target.value)}>
            <option value="">Unassigned</option>
            {profiles.filter(isActiveProfile).map(p => <option key={p.id} value={p.id}>{p.full_name}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Service Area</label>
          <select className="form-control" value={serviceArea} onChange={e => setServiceArea(e.target.value)}>
            {SERVICE_AREAS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Due Date</label>
          <input className="form-control" type="date" value={dateDue} onChange={e => setDateDue(e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Week # (onboarding)</label>
          <input className="form-control" type="number" min="1" value={weekNumber} onChange={e => setWeekNumber(e.target.value)} placeholder="e.g. 1" />
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Approval Path</label>
        <select className="form-control" value={approvalPath} onChange={e => setApprovalPath(e.target.value)}>
          {APPROVAL_PATHS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
        </select>
      </div>
      <div style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
          <input type="checkbox" checked={isOnboarding} onChange={e => setIsOnboarding(e.target.checked)} />
          Onboarding task
        </label>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn btn-gold btn-sm" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Add Task'}</button>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
      </div>
    </div>
  )
}

// ════════════════════════════════════════════════════════════
// EXPORT HELPERS — TXT / PDF / DOCX
// ════════════════════════════════════════════════════════════

// Strip markdown formatting from a line for plain rendering
const stripFormatting = (line) => line.replace(/\*\*(.*?)\*\*/g, '$1').replace(/_(.*?)_/g, '$1')

// Parse markdown content into structured blocks
function parseContent(content) {
  const blocks = []
  for (const line of (content || '').split('\n')) {
    if (line.startsWith('# ')) blocks.push({ type: 'h1', text: line.slice(2) })
    else if (line.startsWith('## ')) blocks.push({ type: 'h2', text: line.slice(3) })
    else if (line.startsWith('### ')) blocks.push({ type: 'h3', text: line.slice(4) })
    else if (line.startsWith('- [ ] ')) blocks.push({ type: 'todo', text: line.slice(6), checked: false })
    else if (line.startsWith('- [x] ')) blocks.push({ type: 'todo', text: line.slice(6), checked: true })
    else if (line.startsWith('- ')) blocks.push({ type: 'bullet', text: line.slice(2) })
    else if (line === '') blocks.push({ type: 'blank' })
    else blocks.push({ type: 'p', text: line })
  }
  return blocks
}

// Format a meeting date for display
const fmtDate = (d) => d ? new Date(d + 'T12:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : ''

// ── TXT export (same as before, kept here as part of the export helpers)
function exportAsTxt({ title, clientName, mtLabel, meetingDate, content, linkedTasks }) {
  const safeName = (title || 'meeting').replace(/[^a-z0-9]/gi, '_')

  const formatLine = (line) => {
    if (line.startsWith('# ')) return '\n' + line.slice(2).toUpperCase() + '\n'
    if (line.startsWith('## ')) return '\n' + line.slice(3) + '\n' + '─'.repeat(30)
    if (line.startsWith('### ')) return line.slice(4)
    if (line.startsWith('- [ ] ')) return '  ☐ ' + line.slice(6)
    if (line.startsWith('- [x] ')) return '  ☑ ' + line.slice(6)
    if (line.startsWith('- ')) return '  • ' + line.slice(2)
    return stripFormatting(line)
  }

  const taskLines = linkedTasks.length > 0 ? [
    '',
    '═'.repeat(50),
    'ACTION ITEMS FROM THIS MEETING',
    '═'.repeat(50),
    '',
    ...linkedTasks.map(t => {
      const assignee = t.assignee?.full_name || null
      const due = t.date_due ? ` · Due: ${new Date(t.date_due + 'T12:00:00').toLocaleDateString()}` : ''
      const status = t.status.replace(/_/g, ' ')
      return `  ☐ ${t.title}${assignee ? ` (${assignee})` : ''}${due} [${status}]`
    }),
  ] : []

  const lines = [
    title,
    '═'.repeat(50),
    `${mtLabel} Meeting`,
    `Date: ${fmtDate(meetingDate)}`,
    clientName ? `Client: ${clientName}` : '',
    '',
    '═'.repeat(50),
    'NOTES',
    '═'.repeat(50),
    '',
    ...(content || '').split('\n').map(formatLine),
    ...taskLines,
    '',
    '─'.repeat(50),
    `Exported from LMR Task Manager · ${new Date().toLocaleDateString()}`,
  ].filter(l => l !== null).join('\n')

  const blob = new Blob([lines], { type: 'text/plain;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${safeName}_${meetingDate || 'meeting'}.txt`
  a.click()
  URL.revokeObjectURL(url)
}

// ── PDF export (using jsPDF)
function exportAsPdf({ title, clientName, mtLabel, meetingDate, content, linkedTasks }) {
  const safeName = (title || 'meeting').replace(/[^a-z0-9]/gi, '_')
  const doc = new jsPDF({ unit: 'pt', format: 'letter' })

  const PAGE_W = doc.internal.pageSize.getWidth()
  const PAGE_H = doc.internal.pageSize.getHeight()
  const MARGIN_X = 54
  const MARGIN_Y = 54
  const CONTENT_W = PAGE_W - MARGIN_X * 2
  let y = MARGIN_Y

  const checkPageBreak = (needed) => {
    if (y + needed > PAGE_H - MARGIN_Y) {
      doc.addPage()
      y = MARGIN_Y
    }
  }

  const writeLine = (text, opts = {}) => {
    const { size = 11, weight = 'normal', color = '#1a2b4a', indent = 0, gap = 4 } = opts
    doc.setFont('helvetica', weight)
    doc.setFontSize(size)
    doc.setTextColor(color)
    const wrapped = doc.splitTextToSize(text, CONTENT_W - indent)
    for (const w of wrapped) {
      checkPageBreak(size * 1.4)
      doc.text(w, MARGIN_X + indent, y)
      y += size * 1.4
    }
    y += gap
  }

  // Title block
  writeLine(title || 'Meeting Notes', { size: 20, weight: 'bold', gap: 6 })

  // Meta line
  const metaParts = []
  if (mtLabel) metaParts.push(`${mtLabel} Meeting`)
  if (meetingDate) metaParts.push(fmtDate(meetingDate))
  if (clientName) metaParts.push(clientName)
  if (metaParts.length) writeLine(metaParts.join(' · '), { size: 11, color: '#666', gap: 8 })

  // Divider
  checkPageBreak(8)
  doc.setDrawColor(180)
  doc.line(MARGIN_X, y, PAGE_W - MARGIN_X, y)
  y += 14

  // Body content
  const blocks = parseContent(content)
  for (const b of blocks) {
    if (b.type === 'h1') writeLine(b.text, { size: 16, weight: 'bold', gap: 4 })
    else if (b.type === 'h2') { y += 6; writeLine(b.text, { size: 13, weight: 'bold', gap: 4 }) }
    else if (b.type === 'h3') writeLine(b.text, { size: 11.5, weight: 'bold', gap: 3 })
    else if (b.type === 'bullet') writeLine('• ' + stripFormatting(b.text), { size: 11, indent: 14, gap: 2 })
    else if (b.type === 'todo') writeLine((b.checked ? '☑ ' : '☐ ') + stripFormatting(b.text), { size: 11, indent: 14, gap: 2 })
    else if (b.type === 'blank') y += 6
    else writeLine(stripFormatting(b.text), { size: 11, gap: 2 })
  }

  // Action items section
  if (linkedTasks.length > 0) {
    y += 18
    checkPageBreak(40)
    doc.setDrawColor(180)
    doc.line(MARGIN_X, y, PAGE_W - MARGIN_X, y)
    y += 14
    writeLine('Action Items', { size: 14, weight: 'bold', gap: 8 })
    for (const t of linkedTasks) {
      const parts = ['☐ ' + t.title]
      if (t.assignee?.full_name) parts.push(`— ${t.assignee.full_name}`)
      if (t.date_due) parts.push(`due ${new Date(t.date_due + 'T12:00:00').toLocaleDateString()}`)
      parts.push(`[${t.status.replace(/_/g, ' ')}]`)
      writeLine(parts.join(' '), { size: 10.5, indent: 8, gap: 3 })
    }
  }

  // Footer (every page)
  const totalPages = doc.internal.getNumberOfPages()
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i)
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)
    doc.setTextColor('#999')
    doc.text(`Exported from LMR Task Manager · ${new Date().toLocaleDateString()}`, MARGIN_X, PAGE_H - 24)
    doc.text(`Page ${i} of ${totalPages}`, PAGE_W - MARGIN_X, PAGE_H - 24, { align: 'right' })
  }

  doc.save(`${safeName}_${meetingDate || 'meeting'}.pdf`)
}

// ── DOCX export (using docx library)
async function exportAsDocx({ title, clientName, mtLabel, meetingDate, content, linkedTasks }) {
  const safeName = (title || 'meeting').replace(/[^a-z0-9]/gi, '_')

  // Build paragraphs
  const children = []

  // Title
  children.push(new Paragraph({
    children: [new TextRun({ text: title || 'Meeting Notes', bold: true, size: 36, color: '1a2b4a' })],
    spacing: { after: 100 },
  }))

  // Meta
  const metaParts = []
  if (mtLabel) metaParts.push(`${mtLabel} Meeting`)
  if (meetingDate) metaParts.push(fmtDate(meetingDate))
  if (clientName) metaParts.push(clientName)
  if (metaParts.length) {
    children.push(new Paragraph({
      children: [new TextRun({ text: metaParts.join(' · '), size: 22, color: '666666' })],
      spacing: { after: 300 },
    }))
  }

  // Content blocks
  const blocks = parseContent(content)
  for (const b of blocks) {
    if (b.type === 'h1') {
      children.push(new Paragraph({
        text: b.text, heading: HeadingLevel.HEADING_1,
        spacing: { before: 240, after: 120 },
      }))
    } else if (b.type === 'h2') {
      children.push(new Paragraph({
        text: b.text, heading: HeadingLevel.HEADING_2,
        spacing: { before: 200, after: 100 },
      }))
    } else if (b.type === 'h3') {
      children.push(new Paragraph({
        text: b.text, heading: HeadingLevel.HEADING_3,
        spacing: { before: 160, after: 80 },
      }))
    } else if (b.type === 'bullet') {
      children.push(new Paragraph({
        children: [new TextRun({ text: '• ' + stripFormatting(b.text) })],
        indent: { left: 360 },
        spacing: { after: 60 },
      }))
    } else if (b.type === 'todo') {
      children.push(new Paragraph({
        children: [new TextRun({ text: (b.checked ? '☑ ' : '☐ ') + stripFormatting(b.text) })],
        indent: { left: 360 },
        spacing: { after: 60 },
      }))
    } else if (b.type === 'blank') {
      children.push(new Paragraph({ children: [], spacing: { after: 100 } }))
    } else {
      children.push(new Paragraph({
        children: [new TextRun({ text: stripFormatting(b.text) })],
        spacing: { after: 80 },
      }))
    }
  }

  // Action items
  if (linkedTasks.length > 0) {
    children.push(new Paragraph({ children: [], spacing: { before: 240 } }))
    children.push(new Paragraph({
      text: 'Action Items', heading: HeadingLevel.HEADING_2,
      spacing: { before: 200, after: 120 },
    }))
    for (const t of linkedTasks) {
      const parts = ['☐ ' + t.title]
      if (t.assignee?.full_name) parts.push(`— ${t.assignee.full_name}`)
      if (t.date_due) parts.push(`due ${new Date(t.date_due + 'T12:00:00').toLocaleDateString()}`)
      parts.push(`[${t.status.replace(/_/g, ' ')}]`)
      children.push(new Paragraph({
        children: [new TextRun({ text: parts.join(' '), size: 21 })],
        indent: { left: 180 },
        spacing: { after: 60 },
      }))
    }
  }

  // Footer note
  children.push(new Paragraph({ children: [], spacing: { before: 400 } }))
  children.push(new Paragraph({
    alignment: AlignmentType.CENTER,
    children: [new TextRun({ text: `Exported from LMR Task Manager · ${new Date().toLocaleDateString()}`, size: 18, color: '999999', italics: true })],
  }))

  const doc = new Document({ sections: [{ children }] })
  const blob = await Packer.toBlob(doc)
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `${safeName}_${meetingDate || 'meeting'}.docx`
  a.click()
  URL.revokeObjectURL(url)
}

// ─── Download menu component ──────────────────────────────────────────────────
function DownloadMenu({ payload }) {
  const [open, setOpen] = useState(false)
  const toast = useToast()

  const run = async (fn, label) => {
    try {
      await fn(payload)
      setOpen(false)
    } catch (e) {
      toast(`${label} export failed: ${e.message}`, 'error')
      console.error(e)
    }
  }

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button className="btn btn-ghost btn-sm" onClick={() => setOpen(o => !o)}>
        <Download size={13} /> Download <ChevronDown size={11} />
      </button>
      {open && (
        <>
          {/* Click-outside backdrop */}
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 99 }} />
          <div style={{
            position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 100,
            background: '#fff', border: '1px solid var(--border)', borderRadius: 8,
            boxShadow: 'var(--shadow-md)', padding: 4, minWidth: 180,
          }}>
            {[
              { label: '📄 Plain Text (.txt)', fn: exportAsTxt, name: 'TXT' },
              { label: '📕 PDF Document (.pdf)', fn: exportAsPdf, name: 'PDF' },
              { label: '📘 Word Doc (.docx)', fn: exportAsDocx, name: 'DOCX' },
            ].map(opt => (
              <button key={opt.name} onClick={() => run(opt.fn, opt.name)}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '8px 12px', border: 'none', background: 'transparent',
                  fontSize: 13, cursor: 'pointer', borderRadius: 4,
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--cream)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                {opt.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

// ─── Meeting note editor ───────────────────────────────────────────────────────
function MeetingEditor({ note, clients, profiles, currentUser, onSaved, onBack }) {
  const toast = useToast()
  const isNew = !note?.id
  const [form, setForm] = useState({
    client_id: note?.client_id || '',
    title: note?.title || '',
    meeting_type: note?.meeting_type || 'weekly',
    meeting_date: note?.meeting_date || format(new Date(), 'yyyy-MM-dd'),
    content: note?.content || '',
  })
  const [linkedTasks, setLinkedTasks] = useState([])
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [showQuickTask, setShowQuickTask] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editing, setEditing] = useState(isNew)
  const [noteId, setNoteId] = useState(note?.id || null)

  const set = (field, val) => setForm(f => ({ ...f, [field]: val }))

  const loadLinkedTasks = useCallback(async () => {
    if (!noteId) return
    const { data } = await supabase.from('tasks').select('*, assignee:profiles!tasks_assigned_to_fkey(full_name)').eq('meeting_note_id', noteId).order('created_at')
    setLinkedTasks(data || [])
  }, [noteId])

  useEffect(() => { loadLinkedTasks() }, [loadLinkedTasks])

  const applyTemplate = (type) => {
    set('meeting_type', type)
    if (TEMPLATES[type] && !form.content) set('content', TEMPLATES[type])
  }

  const save = async () => {
    if (!form.client_id) { toast('Please select a client', 'error'); return }
    if (!form.title.trim()) { toast('Please add a title', 'error'); return }
    setSaving(true)
    try {
      let savedNote
      if (noteId) {
        const { data, error } = await supabase.from('meeting_notes').update({ ...form, updated_at: new Date().toISOString() }).eq('id', noteId).select().single()
        if (error) throw error
        savedNote = data
      } else {
        const { data, error } = await supabase.from('meeting_notes').insert({ ...form, created_by: currentUser.id }).select().single()
        if (error) throw error
        savedNote = data
        setNoteId(savedNote.id)
      }

      // Sync to Google Drive
      supabase.functions.invoke('sync-meeting-note', { body: { note_id: savedNote.id } })

      toast('Notes saved!', 'success')
      setEditing(false)
      onSaved()
    } catch (e) {
      toast('Error: ' + e.message, 'error')
    } finally {
      setSaving(false)
    }
  }

  const mt = MEETING_TYPES.find(m => m.value === form.meeting_type)
  const client = clients.find(c => c.id === form.client_id)

  // Payload shared across all export formats
  const exportPayload = {
    title: form.title,
    clientName: client?.name || '',
    mtLabel: mt?.label || '',
    meetingDate: form.meeting_date,
    content: form.content,
    linkedTasks,
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button className="btn btn-ghost btn-sm" onClick={onBack}><ChevronLeft size={14} /> Back</button>
        <div style={{ flex: 1 }}>
          {editing ? (
            <input className="form-control" value={form.title} onChange={e => set('title', e.target.value)}
              placeholder="Meeting title..." style={{ fontSize: 18, fontFamily: 'var(--font-display)', fontWeight: 400 }} />
          ) : (
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--navy)' }}>{form.title}</h2>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {!editing && <DownloadMenu payload={exportPayload} />}
          {!editing && noteId && (
            <button className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }}
              onClick={() => setConfirmDelete({ id: noteId, title: form.title })}>
              <Trash2 size={13} /> Delete
            </button>
          )}
          {!editing && <button className="btn btn-ghost btn-sm" onClick={() => setEditing(true)}>Edit</button>}
          {editing && (
            <>
              <button className="btn btn-ghost btn-sm" onClick={() => setEditing(false)}>Preview</button>
              <button className="btn btn-gold btn-sm" onClick={save} disabled={saving}>
                <Save size={13} />{saving ? 'Saving...' : 'Save Notes'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Meta row */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {editing ? (
          <>
            <select className="filter-select" value={form.client_id} onChange={e => set('client_id', e.target.value)}>
              <option value="">Select client...</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select className="filter-select" value={form.meeting_type} onChange={e => applyTemplate(e.target.value)}>
              {MEETING_TYPES.map(m => <option key={m.value} value={m.value}>{m.icon} {m.label}</option>)}
            </select>
            <input className="filter-select" type="date" value={form.meeting_date} onChange={e => set('meeting_date', e.target.value)} />
          </>
        ) : (
          <>
            {client && <span className="badge" style={{ background: 'var(--navy-mid)', color: '#fff' }}>{client.name}</span>}
            {mt && <span className="badge" style={{ background: 'var(--cream-dark)', color: 'var(--text-secondary)' }}>{mt.icon} {mt.label}</span>}
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{form.meeting_date && !isNaN(new Date(form.meeting_date + 'T12:00:00')) ? format(new Date(form.meeting_date + 'T12:00:00'), 'MMMM d, yyyy') : ''}</span>
          </>
        )}
      </div>

      {/* Template picker for new notes */}
      {isNew && editing && !form.content && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>Start from a template</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {MEETING_TYPES.map(m => (
              <button key={m.value} className="btn btn-ghost btn-sm"
                style={{ background: form.meeting_type === m.value ? 'var(--navy-mid)' : '', color: form.meeting_type === m.value ? '#fff' : '' }}
                onClick={() => { set('content', TEMPLATES[m.value]); set('meeting_type', m.value) }}>
                {m.icon} {m.label}
              </button>
            ))}
            <button className="btn btn-ghost btn-sm" onClick={() => set('content', '')}>✏️ Blank</button>
          </div>
        </div>
      )}

      {/* Editor / Preview */}
      <div className="card" style={{ overflow: 'hidden', marginBottom: 20 }}>
        {editing
          ? <RichTextEditor value={form.content} onChange={val => set('content', val)} />
          : <div style={{ padding: 20 }}><NotePreview content={form.content} /></div>
        }
      </div>

      {/* Linked tasks */}
      {noteId && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 16, color: 'var(--navy)' }}>Tasks from this meeting</div>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowQuickTask(s => !s)}>
              <Plus size={13} /> Add Task
            </button>
          </div>

          {showQuickTask && (
            <QuickTaskForm
              clientId={form.client_id}
              meetingNoteId={noteId}
              profiles={profiles}
              clients={clients}
              currentUser={currentUser}
              onSaved={(task) => { setShowQuickTask(false); loadLinkedTasks() }}
              onClose={() => setShowQuickTask(false)}
            />
          )}

          {linkedTasks.length === 0 && !showQuickTask && (
            <div style={{ color: 'var(--text-muted)', fontSize: 13, fontStyle: 'italic', padding: '8px 0' }}>
              No tasks linked yet — click "Add Task" to create one from this meeting
            </div>
          )}

          {linkedTasks.map(t => (
            <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: '#fff', border: '1px solid var(--border)', borderRadius: 6, marginBottom: 6 }}>
              <div style={{ flex: 1 }}>
                <span style={{ fontSize: 13, fontWeight: 500 }}>{t.title}</span>
                {t.assignee?.full_name && <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 8 }}>→ {t.assignee.full_name}</span>}
              </div>
              <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: 'var(--cream-dark)', color: 'var(--text-muted)' }}>{t.status.replace(/_/g, ' ')}</span>
              {t.date_due && !isNaN(new Date(t.date_due + 'T12:00:00')) && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{format(new Date(t.date_due + 'T12:00:00'), 'MMM d')}</span>}
            </div>
          ))}
        </div>
      )}

      {!noteId && (
        <div style={{ color: 'var(--text-muted)', fontSize: 13, fontStyle: 'italic' }}>
          Save the note first to start adding tasks
        </div>
      )}

      {confirmDelete && (
        <DeleteNoteModal
          note={confirmDelete}
          onCancel={() => setConfirmDelete(null)}
          onDeleted={() => { setConfirmDelete(null); onBack() }}
        />
      )}
    </div>
  )
}

// ─── Delete confirmation ──────────────────────────────────────────────────────
function DeleteNoteModal({ note, onCancel, onDeleted }) {
  const toast = useToast()
  const [deleting, setDeleting] = useState(false)

  const confirm = async () => {
    setDeleting(true)
    const { error } = await supabase.from('meeting_notes').delete().eq('id', note.id)
    setDeleting(false)
    if (error) { toast('Could not delete: ' + error.message, 'error'); return }
    toast('Meeting note deleted', 'success')
    onDeleted()
  }

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">Delete this meeting note?</div>
          <button className="btn btn-ghost btn-sm" onClick={onCancel}><X size={14} /></button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 13.5, lineHeight: 1.6 }}>
            <strong>{note.title || 'Untitled note'}</strong> will be permanently removed. This cannot be undone.
          </p>
          <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.5 }}>
            Tasks created from this meeting are not deleted — they stay in the task list, just unlinked from the note.
          </p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onCancel} disabled={deleting}>Cancel</button>
          <button className="btn btn-danger" onClick={confirm} disabled={deleting}>
            <Trash2 size={13} /> {deleting ? 'Deleting...' : 'Delete note'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Meeting notes list ───────────────────────────────────────────────────────
export default function MeetingNotesView({ clients, profiles, currentUser, selectedNote, onClearNote }) {
  const [notes, setNotes] = useState([])
  const [selected, setSelected] = useState(null)
  const [creating, setCreating] = useState(false)
  const [filterClient, setFilterClient] = useState('')
  const [filterType, setFilterType] = useState('')
  const [loading, setLoading] = useState(true)
  const [confirmDelete, setConfirmDelete] = useState(null)

  const loadNotes = useCallback(async () => {
    setLoading(true)
    let query = supabase.from('meeting_notes').select('*, client:clients(name)').order('meeting_date', { ascending: false })
    if (filterClient) query = query.eq('client_id', filterClient)
    if (filterType) query = query.eq('meeting_type', filterType)
    const { data } = await query
    setNotes(data || [])
    setLoading(false)
  }, [filterClient, filterType])

  useEffect(() => { loadNotes() }, [loadNotes])

  // Deep link: another view (e.g. the client profile Notes tab) handed us a note
  // to open. The row object is the full meeting_notes record, so open it directly
  // rather than waiting on a fetch or hoping the current filters include it.
  useEffect(() => {
    if (!selectedNote) return
    setCreating(false)
    setSelected(selectedNote)
    if (onClearNote) onClearNote()
  }, [selectedNote, onClearNote])

  if (creating || selected) {
    return (
      <MeetingEditor
        note={selected}
        clients={clients}
        profiles={profiles}
        currentUser={currentUser}
        onSaved={() => { loadNotes() }}
        onBack={() => { setSelected(null); setCreating(false); loadNotes() }}
      />
    )
  }

  return (
    <div>
      <div className="filters-bar" style={{ marginBottom: 20 }}>
        <select className="filter-select" value={filterClient} onChange={e => setFilterClient(e.target.value)}>
          <option value="">All Clients</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select className="filter-select" value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">All Types</option>
          {MEETING_TYPES.map(m => <option key={m.value} value={m.value}>{m.icon} {m.label}</option>)}
        </select>
        <div style={{ marginLeft: 'auto' }}>
          <button className="btn btn-gold" onClick={() => { setSelected(null); setCreating(true) }}>
            <Plus size={15} /> New Meeting Notes
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading...</div>
      ) : notes.length === 0 ? (
        <div className="empty-state">
          <FileText size={48} className="empty-state-icon" />
          <h3>No meeting notes yet</h3>
          <p>Create your first meeting note to get started</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {notes.map(note => {
            const mt = MEETING_TYPES.find(m => m.value === note.meeting_type)
            return (
              <div key={note.id} className="card" style={{ cursor: 'pointer', transition: 'box-shadow 0.15s' }}
                onClick={() => setSelected(note)}
                onMouseEnter={e => e.currentTarget.style.boxShadow = 'var(--shadow-md)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = 'var(--shadow-sm)'}>
                <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ fontSize: 24, width: 40, textAlign: 'center' }}>{mt?.icon || '📝'}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 3 }}>{note.title}</div>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{note.client?.name}</span>
                      <span style={{ fontSize: 11, padding: '1px 7px', borderRadius: 10, background: 'var(--cream-dark)', color: 'var(--text-secondary)' }}>{mt?.label}</span>
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                    {note.meeting_date ? format(new Date(note.meeting_date + 'T12:00:00'), 'MMM d, yyyy') : ''}
                  </div>
                  <button className="btn btn-ghost btn-sm" title="Delete note"
                    style={{ color: 'var(--red)' }}
                    onClick={e => { e.stopPropagation(); setConfirmDelete(note) }}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {confirmDelete && (
        <DeleteNoteModal
          note={confirmDelete}
          onCancel={() => setConfirmDelete(null)}
          onDeleted={() => { setConfirmDelete(null); loadNotes() }}
        />
      )}
    </div>
  )
}